import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getRedirectUrl } from '../utils/redirectUtils';
import { InputField } from '../components/InputField';
import { useError } from '../context/ErrorContext';
import { GoogleLogin } from '@react-oauth/google';

export type LoginType = 'family' | 'teacher' | 'admin';

function Login() {
  const router = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [category, setCategory] = useState<LoginType>('family');
  const { login, loginWithGoogle, auth, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(''); // resetto l'errore precedente
    try {
      await login(email, password, category);
    } catch (err: any) {
      setErrorMessage(err.message);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setErrorMessage('');
    try {
      const token = credentialResponse.credential;
      await loginWithGoogle(token, category);
    } catch (err: any) {
      setErrorMessage(err.message);
    }
  };

  const handleGoogleError = () => {
    setErrorMessage('Errore con Google Sign-In. Riprova o usa email e password.');
  };

  function goToForgotPassword() {
    router('/forgot-password');
  }

  useEffect(() => {
    // Solo se auth è completamente caricato E l'utente è autenticato con un token valido
    if (auth.isAuth === true && auth.token) {
      if (sessionStorage.getItem('googleNewUser') === 'true') {
        router('/register?from=google');
        return;
      }
      const redirectUrl = getRedirectUrl(searchParams, {
        isAdmin: auth.isAdmin,
        isFamily: auth.isFamily,
        isTeacher: auth.isTeacher,
      });

      if (redirectUrl) {
        router(redirectUrl);
        return;
      }

      if (auth.isAdmin === true) {
        router('/admin/home');
      } else if (auth.isTeacher === true) {
        router('/teacher/home');
      } else if (auth.isFamily === true) {
        router('/family/home');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuth, auth.token]);

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] bg-fpcwhite px-4 sm:px-6 w-full mx-auto">
      <div className="my-8 w-full max-w-md">
        <div className="flex">
          <button
            onClick={() => setCategory('family')}
            className={` ${
              category === 'family' ? 'bg-white border-t-8 border-fpcred rounded-tl-3xl' : 'bg-transparent'
            } text-center text-2xl font-bold w-1/2`}>
            Famiglia
          </button>
          <button
            onClick={() => setCategory('teacher')}
            className={` ${
              category === 'teacher' ? 'bg-white border-t-8 border-fpcred rounded-tr-3xl' : 'bg-transparent'
            } text-center text-2xl font-bold w-1/2`}>
            Insegnante
          </button>
        </div>
        <div className="py-5 px-8 bg-white rounded-b-3xl shadow-xl">
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            <h2 className="text-center text-2xl font-bold">{t('login.title').toUpperCase()}</h2>
            {errorMessage && (
              <p className="text-fpcred text-lg text-center bg-fpcpink rounded-lg p-2">{errorMessage}</p>
            )}
            <InputField
              id="email"
              type="email"
              name="email"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              placeholder={t('login.emailPlaceholder')}
              required
              autocomplete="email"
            />
            <InputField
              id="password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              placeholder={t('login.passwordPlaceholder')}
              required
              autocomplete="current-password"
              showPassword={showPassword}
              toggleShowPassword={() => setShowPassword((prev) => !prev)}
            />

            <button type="submit" className="bg-fpcred text-white px-4 py-2 rounded-full">
              {t('login.button')}
            </button>

            {category !== 'teacher' && (
              <>
                <div className="flex items-center gap-2 my-2">
                  <div className="flex-1 border-t border-gray-300"></div>
                  <span className="text-gray-500 text-sm">oppure</span>
                  <div className="flex-1 border-t border-gray-300"></div>
                </div>

                <div className="flex justify-center">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    text="signin_with"
                    use_fedcm_for_prompt={false}
                  />
                </div>
              </>
            )}

            <div className="text-center">
              <p className="text-xl font-normal">
                {t(`login.${category === 'family' ? 'familyNotRegistered' : 'teacherNotRegistered'}`)}
              </p>
              {category === 'family' ? (
                <Link to="/register" className="text-fpcred text-2xl font-bold">
                  {t('login.familyRegister')}
                </Link>
              ) : (
                <Link to="/teacher-application" className="text-fpcred text-2xl font-bold">
                  {t('login.teacherRegister')}
                </Link>
              )}
              <p className="text-xl font-normal text-fpcred cursor-pointer" onClick={goToForgotPassword}>
                Hai dimenticato la password?
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
